import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormTemplateService } from 'app/services/form-template.service';
import { finalize } from 'rxjs/operators';
import { FormBuilder } from '@atefhashem/eforms';
import { FormTemplate } from 'app/models/template';
import { ModalDirective, ToastService } from 'ng-uikit-pro-standard';
import { EcoDocsTitleService } from 'app/services/ecodocs-title.service';
import { FormFillerComponent } from '../form-filler/form-filler.component';
import { FormGroup } from '@angular/forms';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { FormQueryExternalOperation, FormSubmitExternalOperation, FormSubmitExternalOperationParameter } from 'app/models/form-external-operation';
import { ConnectedAppAuth, ConnectedAppEndpoint, ConnectedAppParameter, ObjectType } from 'app/models/connected-app';
import { FieldsPickerComponent } from '../fields-picker/fields-picker.component';
import { ConnectedAppAuthorizeComponent } from '../connected-apps/connected-app-authorize/connected-app-authorize.component';
import { ContactService } from 'app/services/contactService';
import { TeamService } from 'app/services/team.service';
import { UserService } from 'app/services/user.service';
import { DataService } from 'app/services/data.service';
import { loopOnComponents } from 'app/helpers/utils';

@Component({
  selector: 'app-form-builder',
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss']
})
export class FormBuilderComponent implements OnInit {

  isLoadingForm: boolean = true;
  frmBuilder: FormBuilder;
  currentFormTemplate: FormTemplate;
  
  editFormQueryIndex: number = -1;
  editFormSubmitIndex: number = -1;

  deleteFormQueryIndex: number = -1;
  deleteFormSubmitIndex: number = -1;

  currentParameterIndex: number = -1;

  addEditFormQueryFormGroup: FormGroup = null;
  addEditFormSubmitFormGroup: FormGroup = null;

  isAuthorizingQueryExtOp: boolean = false;
  isAuthorizingSubmitExtOp: boolean = false;

  isSavingQueryExtOp: boolean = false;
  isSavingSubmitExtOp: boolean = false;

  isDeletingFormQueryExtOp: boolean = false;
  isDeletingFormSubmitExtOp: boolean = false;

  @ViewChild('formFiller') formFiller: FormFillerComponent;
  @ViewChild('deleteFormQueryExtOpModal') deleteFormQueryExtOpModal: ModalDirective;
  @ViewChild('deleteFormSubmitExtOpModal') deleteFormSubmitExtOpModal: ModalDirective;
  @ViewChild('addEditQueryExtOpModal') addEditQueryExtOpModal: ModalDirective;
  @ViewChild('addEditSubmitExtOpModal') addEditSubmitExtOpModal: ModalDirective;
  @ViewChild('fieldsPicker') fieldsPicker: FieldsPickerComponent;
  @ViewChild('authorizeAppQuery') authorizeAppQuery: ConnectedAppAuthorizeComponent;
  @ViewChild('authorizeAppSubmit') authorizeAppSubmit: ConnectedAppAuthorizeComponent;

  constructor(private formTemplateService: FormTemplateService, private activatedRoute: ActivatedRoute,
              private toastService: ToastService, private router: Router,private ecoDocsTitleService: EcoDocsTitleService,
              private validationHandler: ValidationHandler,
              private dataService: DataService, private userService: UserService, private teamService: TeamService, private contactService: ContactService) { }

  ngOnInit(): void {
    this.ecoDocsTitleService.setTitle('Forms Builder', false);
    this.currentFormTemplate = null;
    if (this.activatedRoute.snapshot.params.id) {
      document.getElementById('eform_builder').innerHTML = "";
      this.isLoadingForm = true;
      this.formTemplateService.getById(this.activatedRoute.snapshot.params.id).pipe(finalize(() => {
        this.isLoadingForm = false;
      })).subscribe((f) => {
        this.currentFormTemplate = f;
        this.frmBuilder = new FormBuilder(document.getElementById('eform_builder'), f.schema, {
          evalContext: {
            getFormQueryExternalOperationObservable: (uniqueName: string, options: any) => {
              return this.formTemplateService.executeFormQueryExternalOperation(this.currentFormTemplate.id, uniqueName, options, this.currentFormTemplate.external_access_token);
            },
            getDataFieldTermsObservable: (indexPattern: string, fieldKey: string, searchFilter: string = '') =>  {
              return this.dataService.getFieldTerms(indexPattern, fieldKey, searchFilter, this.currentFormTemplate.external_access_token);
            },
            getDataRawSearchObservable: (indexPattern: string, body: any) => {
              return this.dataService.rawSearch(indexPattern, body, this.currentFormTemplate.external_access_token);
            },
            getDataSQLQueryObservable: (query: string, format: string = 'json') => {
              return this.dataService.sqlQueryResults(query, format, this.currentFormTemplate.external_access_token);
            },
            /** @note these do not work in external access yet */
            getUsersObservable: () => {
              return this.userService.getAll();
            },
            getTeamsObservable: () => {
              return this.teamService.getAll();
            },
            getContactsObservable: () => {
              return this.contactService.getAll();
            }
          }
        });
      });
    } else {
      /** @todo display errors */
    }
  }

  innerWidth() {
    return window.innerWidth;
  }

  getCurrentFormPath() {
    let formattedPath: string = '';
    if (this.currentFormTemplate) {
      formattedPath = 'Forms ' + this.currentFormTemplate.path.replace(/[\\|\/]/, ' > ');
    }
    return formattedPath;
  }
  onPublishClick() {
    this.isLoadingForm = true;
    this.currentFormTemplate.schema = this.frmBuilder.instance.form;
    this.formTemplateService.putDraft(this.currentFormTemplate).subscribe(() => {
      this.formTemplateService.publish(this.currentFormTemplate.id).pipe(finalize(() => {
        this.isLoadingForm = false;
      })).subscribe(() => {
        this.toastService.success('Published!');
        this.router.navigate(['/forms-explorer', this.currentFormTemplate.path.substr(0, this.currentFormTemplate.path.lastIndexOf('/'))]);
      }, () => {
        this.toastService.error('Error while publishing!');
      });
    });
  }

  onSaveClick() {
    this.currentFormTemplate.schema = this.frmBuilder.instance.form;
    this.isLoadingForm = true;
    this.formTemplateService.putDraft(this.currentFormTemplate).pipe(finalize(() => {
      this.isLoadingForm = false;
    })).subscribe(() => {
      this.toastService.success('Saved!');
    }, () => {
      this.toastService.error('Error while saving!');
    });
  }

  goBack() {
    this.router.navigate(['/forms-explorer', this.currentFormTemplate.path.substr(0, this.currentFormTemplate.path.lastIndexOf('/'))]);
  }

  onPreviewClick() {
    var schema = this.frmBuilder.instance.form;
    loopOnComponents(schema, (cmp) => {
      cmp.id = cmp.id + '-preview';
    });
    const tempForm: FormTemplate = <FormTemplate>{
      id: this.currentFormTemplate.id,
      path: this.currentFormTemplate.path,
      schema: schema
    };

    this.formFiller.show(tempForm);
  }

  addFormSubmitExtOp() {
    this.editFormSubmitIndex = -1;
    this.addEditFormSubmitFormGroup = this.formTemplateService.formSubmitExternalOperationFormGroup();
    this.addEditSubmitExtOpModal.show();
  }

  editFormSubmitExtOpAtIndex(rowIndex: number) {
    this.editFormSubmitIndex = rowIndex;
    this.addEditFormSubmitFormGroup = this.formTemplateService.formSubmitExternalOperationFormGroup(this.currentFormTemplate.form_submit_external_operations[rowIndex]);
    this.addEditSubmitExtOpModal.show();
  }

  deleteFormSubmit() {
    this.isDeletingFormSubmitExtOp = true;
    this.formTemplateService.deleteFormSubmitExternalOperation(this.currentFormTemplate.id, this.currentFormTemplate.form_submit_external_operations[this.deleteFormSubmitIndex].id).pipe(finalize(() => this.isDeletingFormSubmitExtOp = false)).subscribe((qs) => {
      this.currentFormTemplate.form_submit_external_operations = qs;
      this.deleteFormSubmitExtOpModal.hide();
    }, (err) => {
      this.toastService.error('Error Deleting External Submit.');
    });
  }
  
  onFinishedAddEditSubmitExtOp() {
    this.validationHandler.onSubmitCheck(this.addEditFormSubmitFormGroup);
    if (!this.addEditFormSubmitFormGroup.valid) {
      return;
    }

    if (this.addEditFormSubmitFormGroup.value.external_operation_common_settings.auth_methods &&
      this.addEditFormSubmitFormGroup.value.external_operation_common_settings.auth_methods.length > 0 &&
      this.addEditFormSubmitFormGroup.value.external_operation_common_settings.is_per_user_auth == 0) {
        let connectedAppAuths: ConnectedAppAuth[] = [];
        (<FormSubmitExternalOperation>this.addEditFormSubmitFormGroup.value).external_operation_common_settings.auth_methods.forEach((authMethod) => {
          connectedAppAuths.push(authMethod.connected_app_auth);
        });

        this.isAuthorizingSubmitExtOp = true;
        this.authorizeAppSubmit.showIfNeedsAuthorization(connectedAppAuths, true).pipe(finalize(() => this.isAuthorizingSubmitExtOp = false)).subscribe((isAllAuthorized) => {
          if (isAllAuthorized) {
            this.onAddEditSubmitExtOpAuthorizationComplete();
          }
        });
    } else {
      this.onAddEditSubmitExtOpAuthorizationComplete();
    }
  }

  onAddEditSubmitExtOpAuthorizationComplete() {
    this.isSavingSubmitExtOp = true;
    this.formTemplateService.putFormSubmitExternalOperations(this.currentFormTemplate.id, this.addEditFormSubmitFormGroup.value).pipe(finalize(() => this.isSavingSubmitExtOp = false)).subscribe((qs) => {
      this.currentFormTemplate.form_submit_external_operations = qs;
      this.addEditSubmitExtOpModal.hide();
    }, (err) => {
      console.log(err);
      this.toastService.error('Error Saving External Submit.');
    });
  }

  addFormQueryExtOp() {
    this.editFormQueryIndex = -1;
    this.addEditFormQueryFormGroup = this.formTemplateService.formQueryExternalOperationFormGroup();
    this.addEditQueryExtOpModal.show();
  }

  editFormQueryExtOpAtIndex(rowIndex: number) {
    this.editFormQueryIndex = rowIndex;
    this.addEditFormQueryFormGroup = this.formTemplateService.formQueryExternalOperationFormGroup(this.currentFormTemplate.form_query_external_operations[rowIndex]);
    this.addEditQueryExtOpModal.show();
  }

  deleteFormQuery() {
    this.isDeletingFormQueryExtOp = true;
    this.formTemplateService.deleteFormQueryExternalOperation(this.currentFormTemplate.id, this.currentFormTemplate.form_query_external_operations[this.deleteFormQueryIndex].id).pipe(finalize(() => this.isDeletingFormQueryExtOp = false)).subscribe((qs) => {
      this.currentFormTemplate.form_query_external_operations = qs;
      this.deleteFormQueryExtOpModal.hide();
    }, (err) => {
      this.toastService.error('Error Deleting External Query.');
    });
  }

  onFinishedAddEditQueryExtOp() {
    this.validationHandler.onSubmitCheck(this.addEditFormQueryFormGroup);
    if (!this.addEditFormQueryFormGroup.valid) {
      return;
    }

    if (this.addEditFormQueryFormGroup.value.external_operation_common_settings.auth_methods &&
      this.addEditFormQueryFormGroup.value.external_operation_common_settings.auth_methods.length > 0) {
        let connectedAppAuths: ConnectedAppAuth[] = [];
        (<FormQueryExternalOperation>this.addEditFormQueryFormGroup.value).external_operation_common_settings.auth_methods.forEach((authMethod) => {
          connectedAppAuths.push(authMethod.connected_app_auth);
        });

        this.isAuthorizingQueryExtOp = true;
        this.authorizeAppQuery.showIfNeedsAuthorization(connectedAppAuths, this.addEditFormQueryFormGroup.value.external_operation_common_settings.is_per_user_auth == 0).pipe(finalize(() => this.isAuthorizingQueryExtOp = false)).subscribe((isAllAuthorized) => {
          if (isAllAuthorized) {
            this.onAddEditQueryExtOpAuthorizationComplete();
          }
        });
    } else {
      this.onAddEditQueryExtOpAuthorizationComplete();
    }
  }

  onAddEditQueryExtOpAuthorizationComplete() {
    this.isSavingQueryExtOp = true;
    this.formTemplateService.putFormQueryExternalOperations(this.currentFormTemplate.id, this.addEditFormQueryFormGroup.value).pipe(finalize(() => this.isSavingQueryExtOp = false)).subscribe((qs) => {
      this.currentFormTemplate.form_query_external_operations = qs;
      this.addEditQueryExtOpModal.hide();
    }, (err) => {
      console.log(err);
      this.toastService.error('Error Saving External Query.');
    });
  }

  getExtOpBodyModeName() {
    let mode = 'text';
    switch ((<FormGroup>this.addEditFormSubmitFormGroup.controls['external_operation_common_settings']).controls['accepts'].value) {
      case 'application/javascript':
        mode = 'javascript';
        break;
      case 'application/json':
        mode = 'json';
        break;
      case 'test/html':
        mode = 'html';
        break;
      case 'application/xml':
        mode = 'xml';
        break;
    }

    return mode;
  }

  getCurrentPickFieldType() {
    if (this.currentParameterIndex == -1) {
      return [];
    }
    let currentParameter: FormSubmitExternalOperationParameter = this.addEditFormSubmitFormGroup.get(`parameters.${this.currentParameterIndex}`).value;
    let type: ObjectType = (currentParameter.connected_app_parameter.type == ObjectType.ARRAY)? currentParameter.connected_app_parameter.items.type : currentParameter.connected_app_parameter.type;
    let format: string = (currentParameter.connected_app_parameter.type == ObjectType.ARRAY)? currentParameter.connected_app_parameter.items.format : currentParameter.connected_app_parameter.format;
    
    if (type == ObjectType.FILE) {
      return ['file', 'image'];
    } else if (type == ObjectType.INTEGER || type == ObjectType.NUMBER) {
      return ['number', 'currency'];
    } else if (type == ObjectType.BOOLEAN) {
      return ['checkbox'];
    } else if (type == ObjectType.STRING) {
      if (format) {
        if (format == 'email') {
          return ['email'];
        } else if (format == 'url') {
          return ['url'];
        } else if (format == 'date' || format == 'date-time') {
          return ['datetime'];
        } else {
          return ['textfield', 'textarea', 'number', 'checkbox', 'select', 'datadropdown', 'radio', 'email', 'url', 'phoneNumber', 'datetime', 'currency'];
        }
      } else {
        return ['textfield', 'textarea', 'number', 'checkbox', 'select', 'datadropdown', 'radio', 'email', 'url', 'phoneNumber', 'datetime', 'currency'];
      }
    }
  }

  onPickField(parameterIndex: number) {
    this.currentParameterIndex = parameterIndex;
    setTimeout(() => {
      this.fieldsPicker.showPicker(this.frmBuilder.instance.form, this.addEditFormSubmitFormGroup.get(`parameters.${this.currentParameterIndex}.field_submission_path`).value? [this.addEditFormSubmitFormGroup.get(`parameters.${this.currentParameterIndex}.field_submission_path`).value]: null);      
    }, 1);
  }

  onPickedFields(fields: string[]) {
    console.log(fields);
    this.addEditFormSubmitFormGroup.get(`parameters.${this.currentParameterIndex}.field_submission_path`).setValue(fields[0]);
  }

  onEndpointChanged(event: {connectedAppEndpoint: ConnectedAppEndpoint, changedByUser: boolean}) {
    if (event.changedByUser) {
      setTimeout(() => {
        this.formTemplateService.updateSubmitExternalOperationParametersFromEndpoint(this.addEditFormSubmitFormGroup, event.connectedAppEndpoint);      
      }, 1);
    }
  }
}
